import React, { useEffect, useMemo } from 'react';

import { FetchError } from '../../../common/api/api';
import WorkflowStatusSelector, {
  WorkflowStatusSelectorProps
} from '../../workflow/components/WorkflowStatusSelector';
import {
  useFindWorkflowVersionByEntity,
  useWorkflowStatuses
} from '../../workflow/Workflow.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';

export type ProductEntityStatusFilterProps = Pick<
  WorkflowStatusSelectorProps,
  'value' | 'onChange'
>;

const ProductEntityStatusFilter: React.FC<ProductEntityStatusFilterProps> = (
  props: ProductEntityStatusFilterProps
) => {
  const userNeed = useFindWorkflowVersionByEntity(WorkflowEntity.UserNeed);
  const designInput = useFindWorkflowVersionByEntity(
    WorkflowEntity.DesignInput
  );
  const designOutput = useFindWorkflowVersionByEntity(
    WorkflowEntity.DesignOutput
  );
  const verification = useFindWorkflowVersionByEntity(
    WorkflowEntity.DesignVerification
  );
  const validation = useFindWorkflowVersionByEntity(
    WorkflowEntity.DesignValidation
  );
  const error: FetchError | undefined =
    userNeed.loadingError ||
    designInput.loadingError ||
    designOutput.loadingError ||
    verification.loadingError ||
    validation.loadingError;
  const isLoading: boolean =
    userNeed.isLoading ||
    designInput.isLoading ||
    designOutput.isLoading ||
    verification.isLoading ||
    validation.isLoading;

  const userNeedOptions: string[] = useWorkflowStatuses(
    userNeed.workflowVersion
  );
  const designInputOptions: string[] = useWorkflowStatuses(
    designInput.workflowVersion
  );
  const designOutputOptions: string[] = useWorkflowStatuses(
    designOutput.workflowVersion
  );
  const verificationOptions: string[] = useWorkflowStatuses(
    verification.workflowVersion
  );
  const validationOptions: string[] = useWorkflowStatuses(
    validation.workflowVersion
  );

  const options: string[] = useMemo(() => {
    const merged: string[] = ([] as string[]).concat(
      userNeedOptions,
      designInputOptions,
      designOutputOptions,
      verificationOptions,
      validationOptions
    );
    return Array.from(new Set(merged));
  }, [
    userNeedOptions,
    designInputOptions,
    designOutputOptions,
    verificationOptions,
    validationOptions
  ]);

  useEffect(() => {
    userNeed.findWorkflowVersion();
    designInput.findWorkflowVersion();
    designOutput.findWorkflowVersion();
    verification.findWorkflowVersion();
    validation.findWorkflowVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userNeed.findWorkflowVersion,
    designInput.findWorkflowVersion,
    designOutput.findWorkflowVersion,
    verification.findWorkflowVersion,
    validation.findWorkflowVersion
  ]);

  return (
    <WorkflowStatusSelector
      {...props}
      error={error}
      options={options}
      isLoading={isLoading}
      textFieldProps={{ label: undefined, placeholder: 'Select Status' }}
    />
  );
};

export default ProductEntityStatusFilter;
